import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { DeleteOutlined, DownloadOutlined, InfoCircleOutlined, PlusOutlined, ReloadOutlined, RocketOutlined, StopOutlined } from '@ant-design/icons'
import {
  applyExperimentRecommendation,
  BenchmarkItem,
  BenchmarkPack,
  ChapterBenchmarkSourceBook,
  CandidateCostSummary,
  createBenchmarkPack,
  createExperiment,
  createExperimentSeries,
  continueExperimentSeries,
  deleteBenchmarkPack,
  deleteExperimentSeries,
  Experiment,
  ExperimentProgress,
  ExperimentReport,
  ExperimentSeries,
  ExperimentSeriesProgress,
  exportExperimentSeriesReport,
  exportExperimentReport,
  generateChapterBenchmarkPack,
  getExperimentProgress,
  getExperimentReport,
  getExperimentSeriesProgress,
  listBenchmarkPacks,
  listChapterBenchmarkSourceBooks,
  listExperiments,
  listExperimentSeries,
  runExperiment,
  startExperimentSeries,
  stopExperimentSeries,
  updateExperimentSeriesManualPromotions,
} from './AITesting/api_experiments'
import {
  fetchAiTestingModelCatalog,
  fetchDbResultsPaged,
  AITestingModelCatalogEntry,
} from './AITesting/api'
import { TestResultDB } from './AITesting/types'
import './AITestingTab.css'

const { Option } = Select
const { Text, Title } = Typography

const DEFAULT_EVALUATORS = ['Gemini3Pro', 'ChatGPT5.2', 'sonnet45', 'GPT5Test']
type ExperimentPreset = 'quick' | 'standard' | 'loop_optimization' | 'release_candidate'
const TERMINAL_EXPERIMENT_STATUSES = ['completed', 'completed_with_failures', 'failed', 'canceled', 'cancelled']
const POLLING_SERIES_STATUSES = ['running']

const PRESET_OPTIONS: Array<{ value: ExperimentPreset; label: string; short: string; detail: string }> = [
  {
    value: 'quick',
    label: 'Quick',
    short: '1 run, baseline',
    detail: 'Fast smoke test: baseline pipeline only, 1 run per text/model, default judge panel. Use before spending real time or money.',
  },
  {
    value: 'standard',
    label: 'Standard',
    short: '3 runs, selected variants',
    detail: 'Normal comparison run: baseline plus selected rule/pipeline variants, 3 runs per cell, default judge panel. Best default for model and prompt changes.',
  },
  {
    value: 'loop_optimization',
    label: 'Loop Optimization',
    short: 'proofread/alignment matrix',
    detail: 'Tests proofreader loops 1/3/5/8 with final alignment always on. If proofreader models are selected, each model is applied to both formality and general proofreading.',
  },
  {
    value: 'release_candidate',
    label: 'Release Candidate',
    short: '5 runs, highest confidence',
    detail: 'Highest-confidence release check: full selected pack, all selected variants, 5 runs per cell, default judge panel. Use before changing production defaults.',
  },
]

const isAiTestingModelOption = (model: AITestingModelCatalogEntry): boolean => {
  const provider = String(model.provider || '').toLowerCase()
  return provider === 'openai' || provider.includes('openrouter')
}

const DIAGNOSTIC_LABELS: Record<string, string> = {
  html_tag_mismatch: 'HTML/tag preservation failed',
  inline_tag_count_mismatch: 'Inline/paragraph tag count changed',
  empty_or_too_short: 'Output was empty or too short',
  paragraph_count_mismatch: 'Paragraph/block count changed',
  french_dialogue_guillemets_present: 'French dialogue used guillemets',
  source_leakage: 'Source text leaked into output',
  number_drift: 'Number/date/currency drift',
  glossary_violation: 'Glossary rule violation',
  never_translate_violation: 'Never-translate rule violation',
  dialogue_punctuation: 'Dialogue punctuation issue',
}

const diagnosticLabel = (reason: string): string => DIAGNOSTIC_LABELS[reason] || reason.replace(/_/g, ' ')
const variantSafeId = (value: string): string => value.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'model'

const metadataStatusLabel = (status?: string): string => {
  if (status === 'ready') return 'Metadata ready'
  if (status === 'partial') return 'Partial metadata'
  return 'Metadata missing'
}

const metadataStatusColor = (status?: string): string => {
  if (status === 'ready') return 'green'
  if (status === 'partial') return 'gold'
  return 'default'
}

const metadataMissingLabel = (value: string, book?: ChapterBenchmarkSourceBook): string => {
  if (value !== 'quotation_styles') return value.replace(/_/g, ' ')
  const setCount = book?.metadata_fields?.quotation_styles_set_count
  const requiredCount = book?.metadata_fields?.quotation_styles_required_count
  if (typeof setCount === 'number' && typeof requiredCount === 'number') {
    return `${setCount}/${requiredCount} quotation styles`
  }
  return 'quotation styles'
}

const renderQuotationStyleCoverageTooltip = (book: ChapterBenchmarkSourceBook): React.ReactNode => {
  const setLanguages = book.metadata_fields?.quotation_styles_set_languages || []
  const missingLanguages = book.metadata_fields?.quotation_styles_missing_languages || []
  return (
    <Space direction="vertical" size={2}>
      <Text style={{ color: '#102033' }}>Quotation styles by target language</Text>
      <Text style={{ color: '#4c5d73' }}>
        Set: {setLanguages.length ? setLanguages.join(', ') : 'None'}
      </Text>
      {missingLanguages.length ? (
        <Text style={{ color: '#4c5d73' }}>
          Missing: {missingLanguages.join(', ')}
        </Text>
      ) : null}
    </Space>
  )
}

const renderDiagnosticDetails = (
  items?: Array<{ reason: string; count: number; examples?: string[] }>,
  options?: { tooltip?: boolean },
): React.ReactNode => {
  const textColor = options?.tooltip ? '#102033' : undefined
  const secondaryColor = options?.tooltip ? '#4c5d73' : undefined
  if (!items?.length) return <Text style={secondaryColor ? { color: secondaryColor } : undefined} type="secondary">None</Text>
  return (
    <Space direction="vertical" size={4}>
      {items.slice(0, 4).map(item => (
        <div key={item.reason}>
          <Text strong style={textColor ? { color: textColor } : undefined}>{diagnosticLabel(item.reason)}</Text>
          <Text type="secondary" style={secondaryColor ? { color: secondaryColor } : undefined}> ({item.count})</Text>
          {!!item.examples?.length && (
            <div>
              <Text type="secondary" style={secondaryColor ? { color: secondaryColor } : undefined}>Examples: {item.examples.join(', ')}</Text>
            </div>
          )}
        </div>
      ))}
      {items.length > 4 && <Text type="secondary" style={secondaryColor ? { color: secondaryColor } : undefined}>+{items.length - 4} more</Text>}
    </Space>
  )
}

const formatMetric = (value: unknown, digits = 2): string => Number(value || 0).toFixed(digits)
const DECISION_EVALUATION_MODE = 'hard_gate_v1'
const DECISION_GATE_MIN_PASS_RATE = 0.5
const isDecisionResult = (result?: Record<string, any>): boolean => result?.evaluation_mode === DECISION_EVALUATION_MODE
const isDecisionRow = (row?: CandidateCostSummary): boolean => row?.evaluation_mode === DECISION_EVALUATION_MODE || typeof row?.gate_pass_rate === 'number'
const pairwiseComparisonCount = (row?: CandidateCostSummary): number => Number(
  row?.pairwise_comparisons
  ?? (Number(row?.pairwise_wins || 0) + Number(row?.pairwise_losses || 0) + Number(row?.pairwise_ties || 0)),
)
const renderPairwiseResult = (row: CandidateCostSummary): React.ReactNode => {
  const comparisons = pairwiseComparisonCount(row)
  if (!comparisons) return <Text type="secondary">Not run in this phase</Text>
  return (
    <Space direction="vertical" size={0}>
      <Text>{formatMetric(Number(row.pairwise_score || 0) * 100, 0)}%</Text>
      <Text type="secondary">{row.pairwise_wins || 0}W/{row.pairwise_losses || 0}L/{row.pairwise_ties || 0}T · {comparisons} judgments</Text>
    </Space>
  )
}
const seriesPhaseLabel = (phase?: string): string => {
  const labels: Record<string, string> = {
    scout: 'Raw Translation Scout',
    proofreader_sweep: 'Proofreader Combo Sweep',
    loop_sweep: 'Proof Loop Sweep',
    full_validation: 'Full Validation',
  }
  return labels[String(phase || '')] || String(phase || 'Not started')
}

const hardGateIssueRows = (row?: CandidateCostSummary): Array<Record<string, any>> => {
  if (!row) return []
  const summarized = [
    ...((row.critical_issues || []) as Array<Record<string, any>>),
    ...((row.major_issues || []) as Array<Record<string, any>>),
  ]
  if (summarized.length) return summarized.slice(0, 8)
  return ((row.hard_gate_issues || []) as Array<Record<string, any>>)
    .filter(issue => ['critical', 'major'].includes(String(issue.severity || '').toLowerCase()))
    .slice(0, 8)
}

const renderHardGateIssueTable = (rows: Array<Record<string, any>>): React.ReactNode => (
  <Table
    size="small"
    rowKey={(_, index) => `issue-${index}`}
    pagination={false}
    dataSource={rows}
    locale={{ emptyText: 'No critical or major issues recorded' }}
    columns={[
      { title: 'Severity', dataIndex: 'severity', render: value => <Tag color={value === 'critical' ? 'red' : value === 'major' ? 'orange' : 'default'}>{value}</Tag> },
      { title: 'Count', dataIndex: 'count', render: value => Number(value || 1) },
      { title: 'Category', dataIndex: 'category' },
      {
        title: 'Excerpt',
        key: 'translation_excerpt',
        render: (_, issue) => (
          <Space direction="vertical" size={0}>
            <Text>{issue.translation_excerpt || 'n/a'}</Text>
            {issue.examples?.length ? <Text type="secondary">{issue.examples.join(', ')}</Text> : null}
          </Space>
        ),
      },
      { title: 'Explanation', dataIndex: 'explanation' },
    ]}
  />
)

const renderScoringDetails = (row: CandidateCostSummary & Record<string, any>): React.ReactNode => {
  if (isDecisionRow(row)) {
    const issues = hardGateIssueRows(row)
    return (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Row gutter={[16, 8]}>
          <Col xs={12} md={6}>
            <Text type="secondary">Gate pass rate</Text>
            <div><Text strong>{formatMetric(Number(row.gate_pass_rate || 0) * 100, 0)}%</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Critical errors</Text>
            <div><Text strong>{row.critical_error_count || 0}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Major errors</Text>
            <div><Text strong>{row.major_error_count || 0}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Major / 10k words</Text>
            <div><Text strong>{typeof row.major_errors_per_10k_words === 'number' ? formatMetric(row.major_errors_per_10k_words) : 'n/a'}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Hard-gate evaluations</Text>
            <div><Text strong>{row.gate_result_count || 0}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Avg evaluators / run</Text>
            <div><Text strong>{formatMetric(row.avg_evaluators_per_run, 1)}</Text></div>
          </Col>
          <Col xs={24} md={12}>
            <Text type="secondary">Pairwise judging</Text>
            <div>{renderPairwiseResult(row)}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Decision status</Text>
            <div><Tag color={decisionStatusColor(row.decision_status)}>{String(row.decision_status || 'unknown').replace(/_/g, ' ')}</Tag></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Pipeline $/10k words</Text>
            <div><Text strong>{typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}` : 'n/a'}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Pipeline cost</Text>
            <div><Text strong>${formatMetric(pipelineCostTotal(row), 4)}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Evaluation cost</Text>
            <div><Text strong>${formatMetric(evaluationCostTotal(row), 4)}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Total tested</Text>
            <div><Text strong>${formatMetric(row.cost_total_usd || 0, 4)}</Text></div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Source words</Text>
            <div><Text strong>{Math.round(Number(row.source_word_count_total || 0)).toLocaleString()}</Text></div>
          </Col>
        </Row>
        <div>
          <Text strong>Ranking basis</Text>
          <div style={{ marginTop: 4 }}>{renderDecisionRankingBreakdown(row)}</div>
        </div>
        <div>
          <Text strong>Critical / Major Issues</Text>
          {renderHardGateIssueTable(issues)}
        </div>
        <div>
          <Text strong>Usage By Stage</Text>
          <Table
            size="small"
            rowKey="stage"
            pagination={false}
            dataSource={Object.entries(row.usage_by_stage || {}).map(([stage, value]) => ({ stage, ...value }))}
            locale={{ emptyText: 'No usage recorded' }}
            columns={[
              { title: 'Stage', dataIndex: 'stage' },
              { title: 'Tokens', dataIndex: 'total_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
              { title: 'Input', dataIndex: 'input_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
              { title: 'Output', dataIndex: 'output_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
              { title: 'Cost', dataIndex: 'estimated_cost_usd', render: value => `$${formatMetric(value || 0, 4)}` },
              { title: 'Missing Cost', dataIndex: 'missing_cost_entries' },
            ]}
          />
        </div>
      </Space>
    )
  }
  const categories = Object.entries(row.category_breakdown || {}).map(([category, raw]) => {
    const value = raw as { average?: number; count?: number; min?: number; max?: number }
    return {
      category,
      average: value.average,
      count: value.count,
      min: value.min,
      max: value.max,
    }
  })
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Row gutter={[16, 8]}>
        <Col xs={12} md={6}>
          <Text type="secondary">Average score</Text>
          <div><Text strong>{formatMetric(row.average_score)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Scored / completed runs</Text>
          <div><Text strong>{row.scored_run_count || 0} / {row.sample_count || 0}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Judge score values</Text>
          <div><Text strong>{row.judge_score_count || 0}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Avg judges per run</Text>
          <div><Text strong>{formatMetric(row.avg_evaluators_per_run)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Score range</Text>
          <div><Text strong>{formatMetric(row.score_min)} - {formatMetric(row.score_max)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Score std dev</Text>
          <div><Text strong>{formatMetric(row.score_stddev)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Avg tokens</Text>
          <div><Text strong>{Math.round(Number(row.avg_tokens || 0)).toLocaleString()}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Total cost</Text>
          <div><Text strong>${formatMetric(row.cost_total_usd || 0, 4)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Pipeline $/10k words</Text>
          <div><Text strong>{typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}` : 'n/a'}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Pipeline cost</Text>
          <div><Text strong>${formatMetric(pipelineCostTotal(row), 4)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Evaluation cost</Text>
          <div><Text strong>${formatMetric(evaluationCostTotal(row), 4)}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Source words</Text>
          <div><Text strong>{Math.round(Number(row.source_word_count_total || 0)).toLocaleString()}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Missing cost entries</Text>
          <div><Text strong>{row.missing_cost_entries || 0}</Text></div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Avg latency</Text>
          <div><Text strong>{Math.round(Number(row.avg_latency_ms || 0) / 1000)}s</Text></div>
        </Col>
      </Row>
      <div>
        <Text strong>Usage By Stage</Text>
        <Table
          size="small"
          rowKey="stage"
          pagination={false}
          dataSource={Object.entries(row.usage_by_stage || {}).map(([stage, value]) => ({ stage, ...value }))}
          locale={{ emptyText: 'No usage recorded' }}
          columns={[
            { title: 'Stage', dataIndex: 'stage' },
            { title: 'Tokens', dataIndex: 'total_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
            { title: 'Input', dataIndex: 'input_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
            { title: 'Output', dataIndex: 'output_tokens', render: value => Math.round(Number(value || 0)).toLocaleString() },
            { title: 'Service Tier', key: 'service_tier', render: (_, usage) => renderServiceTiers(usage) },
            { title: 'Cost', dataIndex: 'estimated_cost_usd', render: value => `$${formatMetric(value || 0, 4)}` },
            { title: 'Missing Cost', dataIndex: 'missing_cost_entries' },
          ]}
        />
      </div>
      <div>
        <Text strong>Category Scores</Text>
        <Table
          size="small"
          rowKey="category"
          pagination={false}
          dataSource={categories}
          locale={{ emptyText: 'No category scores parsed' }}
          columns={[
            { title: 'Category', dataIndex: 'category' },
            { title: 'Average', dataIndex: 'average', render: value => formatMetric(value) },
            { title: 'Count', dataIndex: 'count' },
            { title: 'Min', dataIndex: 'min', render: value => formatMetric(value) },
            { title: 'Max', dataIndex: 'max', render: value => formatMetric(value) },
          ]}
        />
      </div>
    </Space>
  )
}

const stageLabel = (stage: string): string => stage.replace(/_/g, ' ')

const modelDisplayName = (value?: string | null): string => {
  const raw = String(value || '').trim()
  const labels: Record<string, string> = {
    '': 'Production default',
    production_default: 'Production default',
    'ChatGPT5-2': 'ChatGPT5.2',
    ChatGPT5_2: 'ChatGPT5.2',
    GPT51: 'GPT5.1',
    Gemini3_5Flash: 'Gemini3.5Flash',
    'Gemini3-5Flash': 'Gemini3.5Flash',
  }
  return labels[raw] || raw.replace(/-/g, '.')
}

const variantDetails = (variant?: string | null): { label: string; loops?: number; proofreader?: string | null; raw?: boolean } => {
  const value = String(variant || '').trim()
  if (!value || value === 'baseline') return { label: 'Baseline' }
  if (value === 'raw_translation') return { label: 'Raw translation', raw: true }
  const proofMatch = value.match(/proof(\d+)_align\d+(?:_proof_model_(.+))?$/)
  const loops = proofMatch ? Number(proofMatch[1]) : undefined
  const proofreader = proofMatch ? modelDisplayName(proofMatch[2] || 'production_default') : null
  if (typeof loops === 'number') {
    return {
      label: `${loops} proof ${loops === 1 ? 'loop' : 'loops'} · ${proofreader || 'Production default'}`,
      loops,
      proofreader,
    }
  }
  return { label: value.replace(/_/g, ' ') }
}

const winnerLabel = (winner: any, phase?: string): string => {
  const model = modelDisplayName(winner?.model)
  const loops = Number(winner?.loops || 0)
  if (phase === 'scout' || winner?.variant === 'raw_translation') return `${model} raw`
  const proofreader = winner?.proofreader_model ? modelDisplayName(winner.proofreader_model) : 'Production default'
  return `${model} + ${proofreader}${loops ? `, ${loops} ${loops === 1 ? 'loop' : 'loops'}` : ''}`
}

const candidateLabel = (row: CandidateCostSummary): string => {
  const details = variantDetails(row.variant)
  if (details.raw) return `${modelDisplayName(row.model)} raw translation`
  if (details.loops) return `${modelDisplayName(row.model)} + ${details.proofreader || 'Production default'}, ${details.loops} ${details.loops === 1 ? 'loop' : 'loops'}`
  return `${modelDisplayName(row.model)} · ${details.label}`
}

const candidateKey = (value: { model?: string; variant?: string }): string => `${value.model || 'unknown'}::${value.variant || 'baseline'}`

const selectedCandidateKeys = (result?: Record<string, any>): Set<string> => {
  const winners = (result?.winners || []) as Array<{ model?: string; variant?: string }>
  return new Set(winners.map(winner => candidateKey(winner)))
}

const manualCandidateKeys = (result?: Record<string, any>): Set<string> => {
  const winners = (result?.winners || []) as Array<{ model?: string; variant?: string; manual_promoted?: boolean }>
  return new Set(winners.filter(winner => winner.manual_promoted).map(winner => candidateKey(winner)))
}

const matchingCandidate = (result?: Record<string, any>, winner?: { model?: string; variant?: string }): CandidateCostSummary | undefined => {
  if (!winner) return undefined
  return ((result?.candidate_costs || []) as CandidateCostSummary[]).find(row => candidateKey(row) === candidateKey(winner))
}

const renderCandidateBadges = (row: CandidateCostSummary, selectedKeys: Set<string>, recommendedKey?: string, manualKeys?: Set<string>): React.ReactNode => (
  <Space size={4} wrap>
    {candidateKey(row) === recommendedKey && <Tag color="green">recommended</Tag>}
    {selectedKeys.has(candidateKey(row)) && <Tag color="blue">advanced</Tag>}
    {manualKeys?.has(candidateKey(row)) && <Tag color="purple">manual</Tag>}
    {Number(row.diagnostic_block_rate || 0) > 0 && <Tag color="orange">diagnostic penalty</Tag>}
    {isDecisionRow(row) && <Tag color={decisionStatusColor(row.decision_status)}>{String(row.decision_status || 'decision').replace(/_/g, ' ')}</Tag>}
    {row.pairwise_vs_baseline && <Tag>{row.pairwise_vs_baseline}</Tag>}
  </Space>
)

const decisionStatusColor = (status?: string): string => {
  if (status === 'eligible') return 'green'
  if (status === 'eligible_with_penalties') return 'gold'
  if (status === 'high_failure_rate' || status === 'missing_hard_gates') return 'red'
  return 'orange'
}

const evaluationCostFromStages = (usageByStage?: Record<string, Record<string, any>>): number => (
  Object.entries(usageByStage || {}).reduce((total, [stage, usage]) => (
    ['evaluation', 'pairwise_evaluation'].includes(String(stage).toLowerCase())
      ? total + Number(usage?.estimated_cost_usd || 0)
      : total
  ), 0)
)

const pipelineCostTotal = (row: CandidateCostSummary): number => {
  if (typeof row.pipeline_cost_total_usd === 'number') return row.pipeline_cost_total_usd
  const total = Number(row.cost_total_usd || 0)
  const evaluation = evaluationCostFromStages(row.usage_by_stage)
  return Math.max(total - evaluation, 0)
}

const evaluationCostTotal = (row: CandidateCostSummary): number => {
  if (typeof row.evaluation_cost_total_usd === 'number') return row.evaluation_cost_total_usd
  return evaluationCostFromStages(row.usage_by_stage)
}

const pipelineCostPer10k = (row: CandidateCostSummary): number | null => {
  if (
    row.cost_per_10k_words_basis === 'pipeline_excluding_evaluation'
    && typeof row.cost_per_10k_words === 'number'
  ) {
    return row.cost_per_10k_words
  }
  const words = Number(row.source_word_count_total || 0)
  if (!words) return typeof row.cost_per_10k_words === 'number' ? row.cost_per_10k_words : null
  return pipelineCostTotal(row) / words * 10000
}

const diagnosticPenaltyCount = (row: CandidateCostSummary): number => {
  const blockers = row.diagnostic_blockers || []
  const blockerCount = blockers.reduce((total, item) => total + Math.max(1, Number(item.count || 0)), 0)
  return blockerCount || Number(row.diagnostic_block_rate || 0)
}

const decisionPenaltyValues = (row: CandidateCostSummary): number[] => {
  if (Array.isArray(row.decision_penalty_rank) && row.decision_penalty_rank.length >= 6) {
    return row.decision_penalty_rank.map(value => Number(value || 0)).slice(0, 6)
  }
  const gateShortfall = Math.max(0, DECISION_GATE_MIN_PASS_RATE - Number(row.gate_pass_rate || 0))
  return [
    diagnosticPenaltyCount(row),
    Number(row.critical_error_count || 0),
    Number(row.publication_blocker_count || 0),
    gateShortfall,
    typeof row.major_errors_per_10k_words === 'number' ? row.major_errors_per_10k_words : 999999,
    typeof row.minor_errors_per_10k_words === 'number' ? row.minor_errors_per_10k_words : 999999,
  ]
}

const compareNumberList = (left: number[], right: number[]): number => {
  const length = Math.max(left.length, right.length)
  for (let index = 0; index < length; index += 1) {
    const delta = Number(left[index] || 0) - Number(right[index] || 0)
    if (Math.abs(delta) > 0.000001) return delta
  }
  return 0
}

const compareDecisionCandidates = (left: CandidateCostSummary, right: CandidateCostSummary, pairwiseFirst = false): number => {
  const pairwiseDelta = Number(right.pairwise_score || 0) - Number(left.pairwise_score || 0)
  const penaltyDelta = compareNumberList(decisionPenaltyValues(left), decisionPenaltyValues(right))
  if (pairwiseFirst && Math.abs(pairwiseDelta) > 0.000001) return pairwiseDelta
  if (Math.abs(penaltyDelta) > 0.000001) return penaltyDelta
  if (!pairwiseFirst && Math.abs(pairwiseDelta) > 0.000001) return pairwiseDelta
  const costDelta = Number(pipelineCostPer10k(left) ?? pipelineCostTotal(left)) - Number(pipelineCostPer10k(right) ?? pipelineCostTotal(right))
  if (Math.abs(costDelta) > 0.000001) return costDelta
  return candidateLabel(left).localeCompare(candidateLabel(right))
}

const rankCandidateRows = (rows: CandidateCostSummary[], decisionMode: boolean, pairwiseFirst = false): CandidateCostSummary[] => {
  const filtered = rows.filter(row => decisionMode ? Number(row.sample_count || row.completed_count || 0) > 0 : true)
  if (!decisionMode) return filtered
  return [...filtered].sort((left, right) => compareDecisionCandidates(left, right, pairwiseFirst))
}

const renderDecisionRankingBreakdown = (row: CandidateCostSummary): React.ReactNode => {
  const [diagnostics, critical, blockers, gateShortfall, majorRate, minorRate] = decisionPenaltyValues(row)
  const qualityScore = typeof row.decision_quality_score === 'number' ? row.decision_quality_score : null
  const qualityDelta = typeof row.decision_quality_delta === 'number' ? row.decision_quality_delta : null
  return (
    <Space direction="vertical" size={2}>
      <Text>
        Derived quality {qualityScore === null ? 'n/a' : `${formatMetric(qualityScore, 1)}/100`}
        {qualityDelta !== null ? ` · ${formatMetric(qualityDelta, 1)} behind best` : ''}
      </Text>
      <Text>Penalty order: diagnostics {formatMetric(diagnostics, diagnostics % 1 ? 2 : 0)} · critical {formatMetric(critical, 0)} · publication {formatMetric(blockers, 0)} · gate shortfall {formatMetric(gateShortfall * 100, 0)}%</Text>
      <Text type="secondary">
        Major/10k {majorRate >= 999999 ? 'n/a' : formatMetric(majorRate)} · minor/10k {minorRate >= 999999 ? 'n/a' : formatMetric(minorRate)}
        {' · '}pairwise {formatMetric(Number(row.pairwise_score || 0) * 100, 0)}%
        {' · '}pipeline {typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}/10k` : `$${formatMetric(pipelineCostTotal(row), 4)}`}
      </Text>
    </Space>
  )
}

const renderCompactDecisionQuality = (row: CandidateCostSummary): React.ReactNode => (
  <Space direction="vertical" size={0}>
    <Text strong>{typeof row.decision_quality_score === 'number' ? formatMetric(row.decision_quality_score, 1) : 'n/a'}</Text>
    {typeof row.decision_quality_delta === 'number' && row.decision_quality_delta > 0 && (
      <Text type="secondary">-{formatMetric(row.decision_quality_delta, 1)}</Text>
    )}
  </Space>
)

const renderCompactGateSummary = (row: CandidateCostSummary): React.ReactNode => (
  <Space direction="vertical" size={0}>
    <Text>{formatMetric(Number(row.gate_pass_rate || 0) * 100, 0)}%</Text>
    <Text type="secondary">C{row.critical_error_count || 0} P{row.publication_blocker_count || 0}</Text>
    <Text type="secondary">M{row.major_error_count || 0} m{row.minor_error_count || 0}</Text>
  </Space>
)

const renderCompactDecisionPenalty = (row: CandidateCostSummary): React.ReactNode => {
  const [diagnostics, critical, blockers, gateShortfall, majorRate] = decisionPenaltyValues(row)
  return (
    <Space direction="vertical" size={0}>
      <Text>Diag {formatMetric(diagnostics, diagnostics % 1 ? 2 : 0)} · Gate -{formatMetric(gateShortfall * 100, 0)}%</Text>
      <Text type="secondary">C{formatMetric(critical, 0)} P{formatMetric(blockers, 0)} · Major/10k {majorRate >= 999999 ? 'n/a' : formatMetric(majorRate)}</Text>
    </Space>
  )
}

const renderCompactDecisionSignals = (row: CandidateCostSummary): React.ReactNode => {
  const [diagnostics, critical, blockers, gateShortfall, majorRate] = decisionPenaltyValues(row)
  return (
    <Space direction="vertical" size={0}>
      <Text>
        Gates {formatMetric(Number(row.gate_pass_rate || 0) * 100, 0)}%
        {' · '}C{formatMetric(critical, 0)}/P{formatMetric(blockers, 0)}
      </Text>
      <Text type="secondary">
        M{row.major_error_count || 0} m{row.minor_error_count || 0}
        {' · '}Diag {formatMetric(diagnostics, diagnostics % 1 ? 2 : 0)}
      </Text>
      <Text type="secondary">
        Gate -{formatMetric(gateShortfall * 100, 0)}%
        {' · '}Major/10k {majorRate >= 999999 ? 'n/a' : formatMetric(majorRate)}
      </Text>
    </Space>
  )
}

const renderCompactCostSummary = (row: CandidateCostSummary): React.ReactNode => (
  <Space direction="vertical" size={0}>
    <Text>${formatMetric(row.cost_total_usd || 0, 4)}</Text>
    <Text type="secondary">
      {typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}/10k` : 'n/a /10k'}
    </Text>
    <Text type="secondary">pipe ${formatMetric(pipelineCostTotal(row), 4)} · eval ${formatMetric(evaluationCostTotal(row), 4)}</Text>
  </Space>
)

const formatTierCounts = (counts?: Record<string, unknown>): string => {
  const entries = Object.entries(counts || {})
    .filter(([, count]) => Number(count || 0) > 0)
    .sort((left, right) => String(left[0]).localeCompare(String(right[0])))
  if (!entries.length) return ''
  return entries.map(([tier, count]) => `${tier}: ${Number(count || 0)}`).join(', ')
}

const renderServiceTiers = (usage: Record<string, any>): React.ReactNode => {
  const actual = formatTierCounts(usage.service_tier_counts)
  const requested = formatTierCounts(usage.requested_service_tier_counts)
  if (!actual && !requested) return <Text type="secondary">n/a</Text>
  if (actual && requested && actual !== requested) {
    return (
      <Space direction="vertical" size={0}>
        <Text>{actual}</Text>
        <Text type="secondary">requested: {requested}</Text>
      </Space>
    )
  }
  return <Text>{actual || `requested: ${requested}`}</Text>
}

const renderStageCostSummary = (usageByStage?: Record<string, Record<string, any>>, sourceWords?: number): React.ReactNode => {
  const rows = Object.entries(usageByStage || {})
    .map(([stage, usage]) => ({
      stage,
      cost: Number(usage.estimated_cost_usd || 0),
      costPer10kWords: Number(sourceWords || 0) > 0 ? Number(usage.estimated_cost_usd || 0) / Number(sourceWords || 0) * 10000 : null,
      missing: Number(usage.missing_cost_entries || 0),
      tiers: formatTierCounts(usage.service_tier_counts),
    }))
    .filter(row => row.cost || row.missing)
    .sort((left, right) => right.cost - left.cost)
  if (!rows.length) return <Text type="secondary">No stage costs</Text>
  return (
    <Space direction="vertical" size={2}>
      {rows.slice(0, 4).map(row => (
        <Text key={row.stage}>
          {stageLabel(row.stage)}: ${formatMetric(row.cost, 4)}
          {typeof row.costPer10kWords === 'number' ? ` / $${formatMetric(row.costPer10kWords, 4)}/10k words` : ''}
          {row.tiers ? ` / ${row.tiers}` : ''}
          {row.missing ? ` (${row.missing} missing)` : ''}
        </Text>
      ))}
      {rows.length > 4 && <Text type="secondary">+{rows.length - 4} more</Text>}
    </Space>
  )
}

const renderPairwiseMatrix = (result?: Record<string, any>): React.ReactNode => {
  const matrix = result?.pairwise_summary?.matrix || {}
  const rows = Object.entries(matrix).flatMap(([candidateA, opponents]) => (
    Object.entries((opponents || {}) as Record<string, any>).map(([candidateB, record]) => ({
      pair: `${candidateA} vs ${candidateB}`,
      candidateA,
      candidateB,
      aWins: Number(record?.a_wins || 0),
      bWins: Number(record?.b_wins || 0),
      ties: Number(record?.ties || 0),
    }))
  ))
  if (!rows.length) {
    const isFullValidation = result?.phase === 'full_validation'
    return (
      <Alert
        type={isFullValidation ? 'warning' : 'info'}
        showIcon
        message={isFullValidation ? 'No completed pairwise judgments' : 'Pairwise judging not run in this phase'}
        description={isFullValidation
          ? 'Hard-gate evaluations completed, but the report contains no pairwise comparison records.'
          : 'This screening phase ranks candidates from hard-gate results. Pairwise judging is reserved for Full Validation.'}
      />
    )
  }
  return (
    <div style={{ marginTop: 12 }}>
      <Text strong>Pairwise Matrix</Text>
      <Table
        rowKey="pair"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: 'Candidate A', dataIndex: 'candidateA', render: value => String(value).replace('::', ' / ') },
          { title: 'Candidate B', dataIndex: 'candidateB', render: value => String(value).replace('::', ' / ') },
          { title: 'A wins', dataIndex: 'aWins' },
          { title: 'B wins', dataIndex: 'bWins' },
          { title: 'Ties', dataIndex: 'ties' },
        ]}
      />
    </div>
  )
}

const renderSeriesCostPreview = (result?: Record<string, any>): React.ReactNode => {
  const decisionMode = isDecisionResult(result) || ((result?.candidate_costs || []) as CandidateCostSummary[]).some(row => isDecisionRow(row))
  const pairwiseFirst = decisionMode && result?.phase === 'full_validation'
  const rows = rankCandidateRows(((result?.candidate_costs || []) as CandidateCostSummary[])
    .filter(row => Number(row.cost_total_usd || 0) > 0 || typeof row.average_score === 'number')
    , decisionMode, pairwiseFirst)
  if (!rows.length) return <Text type="secondary">None yet</Text>
  const selectedKeys = selectedCandidateKeys(result)
  const manualKeys = manualCandidateKeys(result)
  const recommendedKey = result?.recommended_action ? candidateKey(result.recommended_action) : undefined
  return (
    <Space direction="vertical" size={6} className="ai-testing-result-list">
      {rows.slice(0, 3).map((row, index) => (
        <div key={row.key || candidateKey(row)} className="ai-testing-result-row">
          <div className="ai-testing-result-title">
            <Text strong>{candidateLabel(row)}</Text>
            {renderCandidateBadges(row, selectedKeys, recommendedKey, manualKeys)}
          </div>
          <Text type="secondary">
            {isDecisionRow(row)
              ? `#${index + 1} · Quality ${typeof row.decision_quality_score === 'number' ? formatMetric(row.decision_quality_score, 1) : 'n/a'} · ${String(row.decision_status || 'decision').replace(/_/g, ' ')} · Gates ${formatMetric(Number(row.gate_pass_rate || 0) * 100, 0)}% · C${row.critical_error_count || 0}/P${row.publication_blocker_count || 0}/M${row.major_error_count || 0}`
              : (typeof row.average_score === 'number' ? `Score ${formatMetric(row.average_score)}` : 'Score n/a')}
            {' · '}
            {typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}/10k pipeline` : 'Pipeline cost n/a'}
            {' · '}
            ${formatMetric(row.cost_total_usd || 0, 4)} total tested
          </Text>
        </div>
      ))}
      {rows.length > 3 && <Text type="secondary">+{rows.length - 3} more</Text>}
    </Space>
  )
}

type SeriesPromotionActions = {
  loading?: boolean
  onPromoteCandidate?: (phase: string, row: CandidateCostSummary) => void
  onPromoteAll?: (phase: string) => void
  onClearPromotions?: (phase: string) => void
}

const renderSeriesCandidateCosts = (result?: Record<string, any>, actions?: SeriesPromotionActions): React.ReactNode => {
  const rawRows = (result?.candidate_costs || []) as CandidateCostSummary[]
  const decisionMode = isDecisionResult(result) || rawRows.some(row => isDecisionRow(row))
  const pairwiseFirst = decisionMode && result?.phase === 'full_validation'
  const rows = rankCandidateRows(rawRows, decisionMode, pairwiseFirst)
  if (!rows.length) return <Text type="secondary">No per-model costs recorded</Text>
  const selectedKeys = selectedCandidateKeys(result)
  const manualKeys = manualCandidateKeys(result)
  const recommendedKey = result?.recommended_action ? candidateKey(result.recommended_action) : undefined
  const issueRows = rows.flatMap(row =>
    hardGateIssueRows(row).slice(0, 3).map(issue => ({
      ...issue,
      candidate: candidateLabel(row),
    })),
  ).slice(0, 12)
  const phase = String(result?.phase || '')
  const canPromote = Boolean(phase && phase !== 'full_validation' && actions?.onPromoteCandidate)
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {canPromote && (
        <Space wrap>
          <Button size="small" onClick={() => actions?.onPromoteAll?.(phase)} loading={actions?.loading}>
            Advance all shown
          </Button>
          <Button size="small" onClick={() => actions?.onClearPromotions?.(phase)} loading={actions?.loading}>
            Clear manual advances
          </Button>
        </Space>
      )}
      <Table
        className="ai-testing-candidate-table"
        rowKey={row => row.key || candidateKey(row)}
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
        ...(decisionMode ? [{
          title: 'Rank',
          key: 'rank',
          width: 64,
          render: (_: unknown, row: CandidateCostSummary, index: number) => (
            <Space direction="vertical" size={0}>
              <Tag color={index === 0 ? 'gold' : index < 3 ? 'blue' : 'default'}>#{index + 1}</Tag>
              {row.decision_eligible === false && <Text type="secondary">not usable</Text>}
            </Space>
          ),
        }] : []),
        ...(decisionMode ? [{
          title: 'Quality',
          key: 'quality',
          width: 78,
          render: (_: unknown, row: CandidateCostSummary) => renderCompactDecisionQuality(row),
        }] : []),
        {
          title: 'Candidate',
          key: 'candidate',
          width: 420,
          render: (_, row: CandidateCostSummary) => (
            <Space direction="vertical" size={2}>
              <Text strong>{candidateLabel(row)}</Text>
              {renderCandidateBadges(row, selectedKeys, recommendedKey, manualKeys)}
            </Space>
          ),
        },
        ...(decisionMode ? [
          {
            title: 'Signals',
            key: 'signals',
            width: 190,
            render: (_: unknown, row: CandidateCostSummary) => renderCompactDecisionSignals(row),
          },
          {
            title: 'Pairwise',
            key: 'pairwise',
            width: 170,
            render: (_: unknown, row: CandidateCostSummary) => renderPairwiseResult(row),
          },
        ] : [
          {
            title: 'Score',
            dataIndex: 'average_score',
            render: (value: unknown) => typeof value === 'number' ? formatMetric(value) : <Text type="secondary">n/a</Text>,
          },
        ]),
        {
          title: 'Runs',
          key: 'runs',
          width: 88,
          render: (_, row: CandidateCostSummary) => {
            const runCount = Number(row.run_count || row.sample_count || 0)
            const completed = Number(row.completed_count || row.sample_count || 0)
            const failed = Number(row.failed_count || 0)
            return `${completed}/${runCount}${failed ? `, ${failed} failed` : ''}`
          },
        },
        {
          title: 'Cost',
          key: 'cost',
          width: 190,
          render: (_: unknown, row: CandidateCostSummary) => renderCompactCostSummary(row),
        },
        ...(canPromote ? [{
          title: 'Manual',
          key: 'manual',
          width: 112,
          render: (_: unknown, row: CandidateCostSummary) => (
            <Button
              size="small"
              onClick={() => actions?.onPromoteCandidate?.(phase, row)}
              disabled={!row.model}
              loading={actions?.loading}
            >
              Advance
            </Button>
          ),
        }] : []),
        ]}
        expandable={decisionMode ? {
          expandedRowRender: (row: CandidateCostSummary) => (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text strong>Ranking breakdown</Text>
                <div style={{ marginTop: 4 }}>{renderDecisionRankingBreakdown(row)}</div>
              </div>
              <div>
                <Text strong>Diagnostics</Text>
                <div style={{ marginTop: 4 }}>{renderDiagnosticDetails(row.diagnostic_blockers)}</div>
              </div>
              <div>
                <Text strong>Stage costs</Text>
                <div style={{ marginTop: 4 }}>{renderStageCostSummary(row.usage_by_stage, row.source_word_count_total)}</div>
              </div>
              {hardGateIssueRows(row).length ? (
                <div>
                  <Text strong>Top hard-gate issues</Text>
                  <div style={{ marginTop: 4 }}>{renderHardGateIssueTable(hardGateIssueRows(row))}</div>
                </div>
              ) : null}
            </Space>
          ),
        } : undefined}
      />
      {decisionMode && (
        <div>
          <Text strong>Critical / Major Issues</Text>
          <Table
            size="small"
            rowKey={(_, index) => `series-issue-${index}`}
            pagination={false}
            dataSource={issueRows}
            locale={{ emptyText: 'No critical or major issues recorded' }}
            columns={[
              { title: 'Candidate', dataIndex: 'candidate' },
              { title: 'Severity', dataIndex: 'severity', render: value => <Tag color={value === 'critical' ? 'red' : value === 'major' ? 'orange' : 'default'}>{value}</Tag> },
              { title: 'Count', dataIndex: 'count', render: value => Number(value || 1) },
              { title: 'Category', dataIndex: 'category' },
              { title: 'Excerpt', dataIndex: 'translation_excerpt', render: value => value || 'n/a' },
              { title: 'Explanation', dataIndex: 'explanation' },
            ]}
          />
        </div>
      )}
      {decisionMode && renderPairwiseMatrix(result)}
    </Space>
  )
}

const renderPhaseOutcome = (phase: string, result?: Record<string, any>): React.ReactNode => {
  if (!result) return <Text type="secondary">Waiting for this phase</Text>
  const recommended = result.recommended_action || (result.winners || [])[0]
  const candidate = matchingCandidate(result, recommended)
  const winners = (result.winners || []) as any[]
  const candidateCostPer10k = candidate ? pipelineCostPer10k(candidate) : null
  const hasDecisionSummary = isDecisionResult(result) || isDecisionRow(candidate)
  return (
    <Space direction="vertical" size={4}>
      {recommended?.model ? (
        <div>
          <Text strong>{winnerLabel(recommended, phase)}</Text>
          {phase === 'full_validation' && <Tag color="green" style={{ marginLeft: 8 }}>final pick</Tag>}
        </div>
      ) : (
        <Text type="secondary">No recommendation yet</Text>
      )}
      <Text type="secondary">
        {hasDecisionSummary
          ? `Gates ${formatMetric(Number(candidate?.gate_pass_rate || recommended?.gate_pass_rate || 0) * 100, 0)}% · Pairwise ${formatMetric(Number(candidate?.pairwise_score || recommended?.pairwise_score || 0) * 100, 0)}%`
          : (typeof recommended?.average_score === 'number' ? `Score ${formatMetric(recommended.average_score)}` : '')}
        {typeof candidateCostPer10k === 'number' ? `${hasDecisionSummary || typeof recommended?.average_score === 'number' ? ' · ' : ''}$${formatMetric(candidateCostPer10k, 4)}/10k pipeline` : ''}
        {candidate?.cost_total_usd ? ` · $${formatMetric(candidate.cost_total_usd, 4)} total tested` : ''}
      </Text>
      {winners.length > 1 && (
        <Space size={4} wrap>
          {winners.slice(0, 4).map((winner, index) => (
            <Tag key={`${winner.model || 'unknown'}-${winner.variant || index}`}>
              {winner.manual_promoted ? 'manual: ' : winner.carried_baseline ? 'baseline: ' : ''}{winnerLabel(winner, phase)}
            </Tag>
          ))}
        </Space>
      )}
    </Space>
  )
}

const renderSeriesDecisionPanel = (progress: ExperimentSeriesProgress): React.ReactNode => {
  const phases = progress.phases || []
  const finalPhase = phases.find(phase => phase.phase === 'full_validation')
  const latestCompleted = [...phases].reverse().find(phase => phase.result?.recommended_action)
  const phase = finalPhase?.result?.recommended_action ? finalPhase : latestCompleted
  const result = phase?.result
  const recommended = result?.recommended_action || (result?.winners || [])[0]
  if (!result || !recommended?.model) return null

  const candidate = matchingCandidate(result, recommended)
  const decisionMode = isDecisionResult(result) || isDecisionRow(candidate)
  const candidateRows = rankCandidateRows((result.candidate_costs || []) as CandidateCostSummary[], decisionMode, result.phase === 'full_validation')
    .filter(row => decisionMode || typeof row.average_score === 'number')
  const blockedHigher = candidateRows.find(row =>
    Number(row.diagnostic_block_rate || 0) > 0
    && typeof candidate?.average_score === 'number'
    && Number(row.average_score || 0) > Number(candidate.average_score || 0)
  )
  const cleanRunnerUp = candidateRows.find(row =>
    candidateKey(row) !== candidateKey(recommended)
    && Number(row.diagnostic_block_rate || 0) === 0
  )
  const candidateCostPer10k = candidate ? pipelineCostPer10k(candidate) : null
  const runnerUpCostPer10k = cleanRunnerUp ? pipelineCostPer10k(cleanRunnerUp) : null

  return (
    <div className="ai-testing-decision-panel">
      <div className="ai-testing-decision-main">
        <Text type="secondary">{phase?.phase === 'full_validation' ? 'Final recommendation' : 'Current recommendation'}</Text>
        <Title level={4} className="ai-testing-decision-title">{winnerLabel(recommended, phase?.phase)}</Title>
        <Space wrap>
          {typeof recommended.average_score === 'number' && <Tag color="green">Score {formatMetric(recommended.average_score)}</Tag>}
          {decisionMode && <Tag color="blue">Quality {formatMetric(candidate?.decision_quality_score || recommended.decision_quality_score || 0, 1)}/100</Tag>}
          {decisionMode && <Tag color="green">Gates {formatMetric(Number(candidate?.gate_pass_rate || recommended.gate_pass_rate || 0) * 100, 0)}%</Tag>}
          {decisionMode && <Tag>Pairwise {formatMetric(Number(candidate?.pairwise_score || recommended.pairwise_score || 0) * 100, 0)}%</Tag>}
          {decisionMode && <Tag>Major errors {candidate?.major_error_count || recommended.major_error_count || 0}</Tag>}
          {typeof candidateCostPer10k === 'number' && <Tag>${formatMetric(candidateCostPer10k, 4)}/10k pipeline</Tag>}
          {candidate && <Tag>${formatMetric(pipelineCostTotal(candidate), 4)} pipeline cost</Tag>}
          {candidate && <Tag>${formatMetric(evaluationCostTotal(candidate), 4)} evaluation cost</Tag>}
          {candidate?.cost_total_usd && <Tag>${formatMetric(candidate.cost_total_usd, 4)} total tested</Tag>}
          {candidate?.diagnostic_block_rate ? <Tag color="orange">{formatMetric(candidate.diagnostic_block_rate * 100, 0)}% diagnostic penalty</Tag> : <Tag color="green">No diagnostic penalty</Tag>}
        </Space>
      </div>
      <div className="ai-testing-decision-notes">
        {decisionMode ? (
          <Alert
            type="success"
            showIcon
            message="Best eligible candidate"
            description="This recommendation is ranked by penalty signals first, then pairwise results and pipeline cost. Critical errors, publication blockers, gate misses, and diagnostics penalize rather than automatically exclude."
          />
        ) : blockedHigher ? (
          <Alert
            type="warning"
            showIcon
            message="Higher raw score was filtered"
            description={`${candidateLabel(blockedHigher)} scored ${formatMetric(blockedHigher.average_score)}, but had ${formatMetric(Number(blockedHigher.diagnostic_block_rate || 0) * 100, 0)}% diagnostic blockers.`}
          />
        ) : (
          <Alert
            type="success"
            showIcon
            message="Best eligible candidate"
            description="This recommendation is based on the highest selected score after diagnostics and phase policy."
          />
        )}
        {cleanRunnerUp && (
          <Text type="secondary">
            Nearest clean comparison: {candidateLabel(cleanRunnerUp)}
            {decisionMode ? ` at pairwise ${formatMetric(Number(cleanRunnerUp.pairwise_score || 0) * 100, 0)}%` : ` at score ${formatMetric(cleanRunnerUp.average_score)}`}
            {typeof runnerUpCostPer10k === 'number' ? `, $${formatMetric(runnerUpCostPer10k, 4)}/10k pipeline` : ''}.
          </Text>
        )}
      </div>
    </div>
  )
}

const LANGUAGES = [
  'French',
  'German',
  'Italian',
  'European Spanish',
  'Latin American Spanish',
  'European Portuguese',
  'Brazilian Portuguese',
  'Dutch',
  'Japanese',
  'Korean',
  'Traditional Chinese',
  'Simplified Chinese',
]

const defaultBenchmarkItems: BenchmarkItem[] = [
  {
    name: 'Dialogue and formatting sample',
    source_text: '<p>"Hello," she said. "I paid $12.50 on May 3."</p>',
    tags: ['dialogue', 'numbers', 'html'],
    category: 'smoke',
    difficulty: 'medium',
  },
]

const AIExperimentTestingTab: React.FC = () => {
  const [language, setLanguage] = useState('French')
  const [packs, setPacks] = useState<BenchmarkPack[]>([])
  const [selectedPackId, setSelectedPackId] = useState<string>()
  const [models, setModels] = useState<AITestingModelCatalogEntry[]>([])
  const [translationModels, setTranslationModels] = useState<string[]>([])
  const [evaluatorModels, setEvaluatorModels] = useState<string[]>(DEFAULT_EVALUATORS)
  const [preset, setPreset] = useState<ExperimentPreset>('standard')
  const [numRuns, setNumRuns] = useState<number | undefined>(undefined)
  const [experimentName, setExperimentName] = useState('')
  const [extraInstruction, setExtraInstruction] = useState('')
  const [proofreaderTestModels, setProofreaderTestModels] = useState<string[]>([])
  const [proofreaderAlignmentModel, setProofreaderAlignmentModel] = useState<string | undefined>(undefined)
  const [seriesName, setSeriesName] = useState('')
  const [seriesBaselineModel, setSeriesBaselineModel] = useState<string | undefined>(undefined)
  const [seriesBudgetCap, setSeriesBudgetCap] = useState<number | undefined>(undefined)
  const [pauseBetweenPhases, setPauseBetweenPhases] = useState(true)
  const [seriesRows, setSeriesRows] = useState<ExperimentSeries[]>([])
  const [activeSeries, setActiveSeries] = useState<ExperimentSeries | null>(null)
  const [seriesProgress, setSeriesProgress] = useState<ExperimentSeriesProgress | null>(null)
  const [activeExperiment, setActiveExperiment] = useState<Experiment | null>(null)
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [progress, setProgress] = useState<ExperimentProgress | null>(null)
  const [report, setReport] = useState<ExperimentReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [packModalOpen, setPackModalOpen] = useState(false)
  const [chapterPackModalOpen, setChapterPackModalOpen] = useState(false)
  const [chapterSourceBooks, setChapterSourceBooks] = useState<ChapterBenchmarkSourceBook[]>([])
  const [chapterSourceBooksLoading, setChapterSourceBooksLoading] = useState(false)
  const [legacyResults, setLegacyResults] = useState<TestResultDB[]>([])
  const [packForm] = Form.useForm()
  const [chapterPackForm] = Form.useForm()

  const selectedPack = useMemo(
    () => packs.find(pack => pack.id === selectedPackId),
    [packs, selectedPackId],
  )

  const selectedPresetInfo = useMemo(
    () => PRESET_OPTIONS.find(item => item.value === preset) || PRESET_OPTIONS[1],
    [preset],
  )

  const chapterSourceBookMetadataCounts = useMemo(() => {
    return chapterSourceBooks.reduce(
      (acc, book) => {
        const status = book.metadata_status || 'missing'
        if (status === 'ready') acc.ready += 1
        else if (status === 'partial') acc.partial += 1
        else acc.missing += 1
        return acc
      },
      { ready: 0, partial: 0, missing: 0 },
    )
  }, [chapterSourceBooks])

  const modelOptions = useMemo(
    () => models.filter(isAiTestingModelOption),
    [models],
  )

  const loadInitialData = async (): Promise<void> => {
    setLoading(true)
    try {
      const [packRows, modelRows, legacyRows] = await Promise.all([
        listBenchmarkPacks(language),
        fetchAiTestingModelCatalog(true),
        fetchDbResultsPaged(language, 20, 0),
      ])
      const [experimentRows, seriesList] = await Promise.all([
        listExperiments(language, 25),
        listExperimentSeries(language, 25),
      ])
      setPacks(packRows)
      setModels(modelRows)
      setExperiments(experimentRows)
      setSeriesRows(seriesList)
      const configuredTestingModelIds = new Set(modelRows.filter(isAiTestingModelOption).map(model => model.id))
      setEvaluatorModels(prev => {
        const retained = prev.filter(modelId => configuredTestingModelIds.has(modelId))
        if (retained.length > 0) return retained
        return DEFAULT_EVALUATORS.filter(modelId => configuredTestingModelIds.has(modelId))
      })
      setLegacyResults(legacyRows || [])
      if (!selectedPackId && packRows[0]) setSelectedPackId(packRows[0].id)
    } catch (error) {
      notification.error({ message: 'Failed to load AI testing data', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language])

  const activeExperimentId = activeExperiment?.id
  const activeExperimentStatus = activeExperiment?.status
  const activeSeriesId = activeSeries?.id
  const activeSeriesStatus = activeSeries?.status

  useEffect(() => {
    if (!activeExperimentId || !activeExperimentStatus || TERMINAL_EXPERIMENT_STATUSES.includes(activeExperimentStatus)) return
    let refreshing = false
    const timer = window.setInterval(async () => {
      if (refreshing) return
      refreshing = true
      try {
        const next = await getExperimentProgress(activeExperimentId)
        setProgress(next)
        setActiveExperiment(prev => prev && prev.id === activeExperimentId ? { ...prev, status: next.status } : prev)
        if (TERMINAL_EXPERIMENT_STATUSES.includes(next.status)) {
          const nextReport = await getExperimentReport(activeExperimentId)
          setReport(nextReport)
          window.clearInterval(timer)
        }
      } catch (error) {
        notification.warning({ message: 'Experiment progress refresh failed', description: String(error) })
      } finally {
        refreshing = false
      }
    }, 5000)
    return () => window.clearInterval(timer)
  }, [activeExperimentId, activeExperimentStatus])

  useEffect(() => {
    if (!activeSeriesId || !activeSeriesStatus || !POLLING_SERIES_STATUSES.includes(activeSeriesStatus)) return
    let refreshing = false
    const timer = window.setInterval(async () => {
      if (refreshing) return
      refreshing = true
      try {
        const next = await getExperimentSeriesProgress(activeSeriesId)
        setSeriesProgress(next)
        setActiveSeries(next.series)
      } catch (error) {
        notification.warning({ message: 'Tiered testing progress refresh failed', description: String(error) })
      } finally {
        refreshing = false
      }
    }, 7000)
    return () => window.clearInterval(timer)
  }, [activeSeriesId, activeSeriesStatus])

  const handleCreatePack = async (values: any): Promise<void> => {
    try {
      const parsedItems = values.itemsJson ? JSON.parse(values.itemsJson) : defaultBenchmarkItems
      const created = await createBenchmarkPack({
        name: values.name,
        language,
        version: values.version || 'v1',
        description: values.description,
        items: parsedItems,
      })
      setPacks(prev => [created, ...prev.filter(pack => pack.id !== created.id)])
      setSelectedPackId(created.id)
      setPackModalOpen(false)
      packForm.resetFields()
    } catch (error) {
      notification.error({ message: 'Failed to create benchmark pack', description: String(error) })
    }
  }

  const loadChapterSourceBooks = async (minWordCount?: number): Promise<void> => {
    setChapterSourceBooksLoading(true)
    try {
      const rows = await listChapterBenchmarkSourceBooks({
        min_word_count: Number(minWordCount || chapterPackForm.getFieldValue('minWordCount') || 1000),
        max_books_to_scan: Number(chapterPackForm.getFieldValue('maxBooksToScan') || 500),
      })
      setChapterSourceBooks(rows)
    } catch (error) {
      notification.error({ message: 'Failed to load source books', description: String(error) })
    } finally {
      setChapterSourceBooksLoading(false)
    }
  }

  const handleOpenChapterPackModal = async (): Promise<void> => {
    setChapterPackModalOpen(true)
    chapterPackForm.setFieldsValue({
      minWordCount: chapterPackForm.getFieldValue('minWordCount') || 1000,
      maxBooksToScan: chapterPackForm.getFieldValue('maxBooksToScan') || 500,
    })
    await loadChapterSourceBooks()
  }

  const handleGenerateChapterPack = async (values: any): Promise<void> => {
    setLoading(true)
    try {
      const created = await generateChapterBenchmarkPack({
        name: values.name,
        language: 'Any',
        version: values.version || 'v1',
        description: values.description,
        min_word_count: Number(values.minWordCount || 1000),
        max_chapters: Number(values.maxChapters || 5),
        random_seed: values.randomSeed || undefined,
        max_books_to_scan: Number(values.maxBooksToScan || 100),
        source_book_ids: values.sourceBookIds || [],
      })
      setPacks(prev => [created, ...prev.filter(pack => pack.id !== created.id)])
      setSelectedPackId(created.id)
      setChapterPackModalOpen(false)
      chapterPackForm.resetFields()
      notification.success({ message: 'Full-chapter benchmark pack generated' })
    } catch (error) {
      notification.error({ message: 'Failed to generate chapter pack', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleOpenExperiment = async (experiment: Experiment): Promise<void> => {
    setLoading(true)
    try {
      setActiveExperiment(experiment)
      setProgress(await getExperimentProgress(experiment.id))
      try {
        setReport(await getExperimentReport(experiment.id))
      } catch {
        setReport(null)
      }
    } catch (error) {
      notification.error({ message: 'Failed to open experiment', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const refreshSeriesProgress = async (series: ExperimentSeries): Promise<void> => {
    const next = await getExperimentSeriesProgress(series.id)
    setActiveSeries(next.series)
    setSeriesProgress(next)
    setSeriesRows(prev => [next.series, ...prev.filter(row => row.id !== next.series.id)])
  }

  const handleOpenSeries = async (series: ExperimentSeries): Promise<void> => {
    setLoading(true)
    try {
      await refreshSeriesProgress(series)
    } catch (error) {
      notification.error({ message: 'Failed to open tiered test series', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAndStartSeries = async (): Promise<void> => {
    if (!selectedPack) {
      notification.warning({ message: 'Select a benchmark pack first' })
      return
    }
    if (!translationModels.length) {
      notification.warning({ message: 'Select at least one translation model' })
      return
    }
    if (!evaluatorModels.length || evaluatorModels.length > 6) {
      notification.warning({ message: 'Select 1 to 6 evaluator models' })
      return
    }
    const baseline = seriesBaselineModel || translationModels[0]
    if (!baseline || !translationModels.includes(baseline)) {
      notification.warning({ message: 'Select a baseline model included in the translation models' })
      return
    }

    setLoading(true)
    try {
      const created = await createExperimentSeries({
        name: seriesName || `${language} tiered testing series`,
        target_language: language,
        benchmark_pack_id: selectedPack.id,
        benchmark_pack_version: selectedPack.version,
        translation_models: translationModels,
        proofreader_models: proofreaderTestModels,
        evaluator_models: evaluatorModels,
        baseline_model: baseline,
        budget_cap_usd: seriesBudgetCap,
        pause_between_phases: pauseBetweenPhases,
      })
      const started = await startExperimentSeries(created.id)
      setActiveSeries(started)
      setSeriesRows(prev => [started, ...prev.filter(row => row.id !== started.id)])
      setSeriesProgress(await getExperimentSeriesProgress(started.id))
      notification.success({ message: 'Tiered testing started', description: seriesPhaseLabel(started.current_phase) })
    } catch (error) {
      notification.error({ message: 'Failed to start tiered testing', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleContinueSeries = async (): Promise<void> => {
    if (!activeSeries) return
    setLoading(true)
    try {
      const continued = await continueExperimentSeries(activeSeries.id)
      setActiveSeries(continued)
      setSeriesProgress(await getExperimentSeriesProgress(continued.id))
      notification.success({ message: 'Tiered testing continued' })
    } catch (error) {
      notification.error({ message: 'Failed to continue tiered testing', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleManualPromotion = async (
    phase: string,
    mode: 'add' | 'clear' | 'all',
    row?: CandidateCostSummary,
  ): Promise<void> => {
    if (!activeSeries) return
    setLoading(true)
    try {
      const next = await updateExperimentSeriesManualPromotions(activeSeries.id, {
        phase,
        mode,
        candidates: row ? [{ model: row.model, variant: row.variant }] : [],
      })
      setActiveSeries(next.series)
      setSeriesProgress(next)
      setSeriesRows(prev => [next.series, ...prev.filter(series => series.id !== next.series.id)])
      notification.success({
        message: mode === 'clear' ? 'Manual advances cleared' : 'Manual advance saved',
        description: 'Later phase results were reset so the next run uses the updated candidate set.',
      })
    } catch (error) {
      notification.error({ message: 'Failed to update manual advances', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleStopSeries = async (): Promise<void> => {
    if (!activeSeries) return
    Modal.confirm({
      title: 'Stop tiered testing series?',
      content: 'No later phases will be created. The submitted experiment will continue until it finishes.',
      okText: 'Stop Series',
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true)
        try {
          const stopped = await stopExperimentSeries(activeSeries.id)
          setActiveSeries(stopped)
          setSeriesProgress(await getExperimentSeriesProgress(stopped.id))
          notification.success({ message: 'Tiered testing stopped' })
        } catch (error) {
          notification.error({ message: 'Failed to stop tiered testing', description: String(error) })
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleDeleteSeries = async (series: ExperimentSeries): Promise<void> => {
    Modal.confirm({
      title: 'Delete tiered testing series?',
      content: 'Stop future phases and wait for all submitted jobs to finish before deleting this series and its results.',
      okText: 'Delete Series',
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true)
        try {
          await deleteExperimentSeries(series.id)
          setSeriesRows(prev => prev.filter(row => row.id !== series.id))
          if (activeSeries?.id === series.id) {
            setActiveSeries(null)
            setSeriesProgress(null)
          }
          notification.success({ message: 'Tiered testing series deleted' })
        } catch (error) {
          notification.error({ message: 'Failed to delete tiered testing series', description: String(error) })
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleDeleteSelectedPack = async (): Promise<void> => {
    if (!selectedPack) {
      notification.warning({ message: 'Select a benchmark pack first' })
      return
    }
    Modal.confirm({
      title: 'Delete benchmark pack?',
      content: `Delete "${selectedPack.name}" (${selectedPack.version})? Active experiments or tiered series that still reference this pack will block deletion.`,
      okText: 'Delete Pack',
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true)
        try {
          await deleteBenchmarkPack(selectedPack.id)
          const remainingPacks = packs.filter(pack => pack.id !== selectedPack.id)
          setPacks(remainingPacks)
          setSelectedPackId(current => current === selectedPack.id ? remainingPacks[0]?.id : current)
          notification.success({ message: 'Benchmark pack deleted' })
        } catch (error) {
          notification.error({ message: 'Failed to delete benchmark pack', description: String(error) })
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleExportSeriesReport = async (detail: 'summary' | 'detailed', series: ExperimentSeries | null = activeSeries): Promise<void> => {
    if (!series) return
    setLoading(true)
    try {
      const payload = await exportExperimentSeriesReport(series.id, detail)
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `ai-testing-series-${series.id}-${detail}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      notification.success({ message: `${detail === 'summary' ? 'Summary' : 'Detailed'} series JSON exported` })
    } catch (error) {
      notification.error({ message: 'Failed to export series report JSON', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAndRunExperiment = async (): Promise<void> => {
    if (!selectedPack) {
      notification.warning({ message: 'Select a benchmark pack first' })
      return
    }
    if (!translationModels.length) {
      notification.warning({ message: 'Select at least one translation model' })
      return
    }
    if (!evaluatorModels.length || evaluatorModels.length > 6) {
      notification.warning({ message: 'Select 1 to 6 evaluator models' })
      return
    }

    setLoading(true)
    try {
      const baseVariant = {
        id: 'baseline',
        label: 'Baseline',
        extra_translation_instruction: '',
        proofreader_enabled: true,
        dialogue_normalizer_enabled: true,
        translation_max_retries: 2,
        proofreader_loops: 1,
        run_final_alignment: true,
        proofreader_formality_model: undefined,
        proofreader_general_model: undefined,
        proofreader_alignment_model: proofreaderAlignmentModel,
      }
      const proofreaderTemplates = proofreaderTestModels.length
        ? proofreaderTestModels.map(modelId => ({
          ...baseVariant,
          id: `proof_model_${variantSafeId(modelId)}`,
          label: `Proofreader ${modelId}`,
          proofreader_formality_model: modelId,
          proofreader_general_model: modelId,
        }))
        : [baseVariant]
      const variants = [...proofreaderTemplates]
      if (extraInstruction.trim()) {
        proofreaderTemplates.forEach(template => variants.push({
          ...template,
          id: `${template.id}_rules`,
          label: `${template.label || template.id} + Rules`,
          extra_translation_instruction: extraInstruction.trim(),
        }))
      }
      const experiment = await createExperiment({
        name: experimentName || `${language} ${preset} experiment`,
        target_language: language,
        benchmark_pack_id: selectedPack.id,
        benchmark_pack_version: selectedPack.version,
        translation_models: translationModels,
        evaluator_models: evaluatorModels,
        preset,
        baseline_model: translationModels[0],
        pipeline_variants: variants,
        num_runs: numRuns,
        apply_targets: {},
      })
      setActiveExperiment(experiment)
      setExperiments(prev => [experiment, ...prev.filter(row => row.id !== experiment.id)])
      setProgress({
        experiment_id: experiment.id,
        status: 'draft',
        total_cells: experiment.total_cells,
        progress_percentage: 0,
        counts: { draft: experiment.total_cells },
        runs: [],
      })
      setReport(null)
      notification.info({ message: 'Experiment created', description: `${experiment.total_cells || 0} cells are being queued.` })
      runExperiment(experiment.id)
        .then(async runResult => {
          setActiveExperiment({ ...experiment, status: String(runResult.status || 'running') })
          const nextProgress = await getExperimentProgress(experiment.id)
          setProgress(nextProgress)
          notification.success({ message: 'Experiment started', description: `${runResult.queued_cells || 0} cells queued.` })
        })
        .catch(error => {
          notification.error({ message: 'Failed to queue experiment', description: String(error) })
          setActiveExperiment({ ...experiment, status: 'failed' })
        })
    } catch (error) {
      notification.error({ message: 'Failed to start experiment', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleRefreshReport = async (): Promise<void> => {
    if (!activeExperiment) return
    setLoading(true)
    try {
      setProgress(await getExperimentProgress(activeExperiment.id))
      setReport(await getExperimentReport(activeExperiment.id))
    } catch (error) {
      notification.error({ message: 'Failed to refresh report', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleExportReport = async (detail: 'summary' | 'detailed'): Promise<void> => {
    if (!activeExperiment) return
    setLoading(true)
    try {
      const payload = await exportExperimentReport(activeExperiment.id, detail)
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `ai-testing-${activeExperiment.id}-${detail}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      notification.success({ message: `${detail === 'summary' ? 'Summary' : 'Detailed'} JSON exported` })
    } catch (error) {
      notification.error({ message: 'Failed to export report JSON', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleResumeQueueing = async (): Promise<void> => {
    if (!activeExperiment) return
    setLoading(true)
    try {
      const result = await runExperiment(activeExperiment.id)
      setActiveExperiment({ ...activeExperiment, status: String(result.status || 'queueing') })
      setProgress(await getExperimentProgress(activeExperiment.id))
      notification.success({ message: 'Experiment queueing resumed' })
    } catch (error) {
      notification.error({ message: 'Failed to resume experiment queueing', description: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async (): Promise<void> => {
    if (!activeExperiment || !report?.recommended_action?.model) return
    const recommendedModel = report.recommended_action.model
    const recommendedTier = report.recommended_action.tier || 'Best'
    Modal.confirm({
      title: 'Apply experiment recommendation?',
      content: `This will update ${language} model configuration using experiment ${activeExperiment.id}.`,
      okText: 'Apply',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true)
        try {
          await applyExperimentRecommendation(activeExperiment.id, {
            confirm: true,
            language,
            updates: [{ modelId: recommendedModel, tier: recommendedTier }],
          })
          notification.success({ message: 'Experiment recommendation applied' })
        } catch (error) {
          notification.error({ message: 'Failed to apply recommendation', description: String(error) })
        } finally {
          setLoading(false)
        }
      },
    })
  }

  return (
    <div className="ai-testing-page">
      <Card className="ai-testing-header-card">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Text type="secondary">New experiment system</Text>
            <Title level={3} style={{ marginBottom: 0 }}>AI Translation Testing</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadInitialData} loading={loading}>Refresh</Button>
              <Button icon={<PlusOutlined />} onClick={() => setPackModalOpen(true)}>New Benchmark Pack</Button>
              <Button icon={<PlusOutlined />} onClick={handleOpenChapterPackModal}>Generate Chapter Pack</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs
        defaultActiveKey="tiered"
        items={[
          {
            key: 'tiered',
            label: 'Tiered Testing',
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Automatic Tiered Testing" className="ai-testing-section-card">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Text strong>Target Language</Text>
                      <Select value={language} onChange={setLanguage} style={{ width: '100%', marginTop: 8 }}>
                        {LANGUAGES.map(item => <Option key={item} value={item}>{item}</Option>)}
                      </Select>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Benchmark Pack</Text>
                      <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                        <Select
                          value={selectedPackId}
                          onChange={setSelectedPackId}
                          style={{ width: '100%' }}
                          placeholder="Select a pack"
                        >
                          {packs.map(pack => (
                            <Option key={`series-pack-${pack.id}`} value={pack.id}>{pack.name} ({pack.version})</Option>
                          ))}
                        </Select>
                        <Tooltip title="Delete selected pack">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={!selectedPack}
                            onClick={handleDeleteSelectedPack}
                          />
                        </Tooltip>
                      </Space.Compact>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Series Name</Text>
                      <Input
                        value={seriesName}
                        onChange={event => setSeriesName(event.target.value)}
                        placeholder={`${language} tiered testing series`}
                        style={{ marginTop: 8 }}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Baseline Model</Text>
                      <Select
                        value={seriesBaselineModel || translationModels[0]}
                        onChange={setSeriesBaselineModel}
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="Select baseline"
                      >
                        {translationModels.map(modelId => (
                          <Option key={`series-baseline-${modelId}`} value={modelId}>{modelId}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Budget Cap</Text>
                      <InputNumber
                        min={0}
                        value={seriesBudgetCap}
                        onChange={value => setSeriesBudgetCap(value || undefined)}
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="Optional USD cap"
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Phase Control</Text>
                      <div style={{ marginTop: 12 }}>
                        <Checkbox
                          checked={pauseBetweenPhases}
                          onChange={(event: any) => setPauseBetweenPhases(Boolean(event?.target?.checked ?? event))}
                        >
                          Pause between phases
                        </Checkbox>
                      </div>
                    </Col>
                  </Row>
                  <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    message="Tiered flow"
                    description="Scout raw translation quality, reuse those translations to compare proofreader combinations, sweep proof loop counts for finalists, then validate the final pipelines end to end."
                  />
                </Card>

                <Card title="Series Models and Judges" className="ai-testing-section-card">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Text strong>Translation Models</Text>
                      <Checkbox.Group value={translationModels} onChange={values => setTranslationModels(values as string[])} style={{ width: '100%', marginTop: 12 }}>
                        <Row gutter={[8, 8]}>
                          {modelOptions.map(model => (
                            <Col xs={24} key={`series-tm-${model.id}`}>
                              <Checkbox value={model.id}>{model.id}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Proofreader Models</Text>
                      <Checkbox.Group value={proofreaderTestModels} onChange={values => setProofreaderTestModels(values as string[])} style={{ width: '100%', marginTop: 12 }}>
                        <Row gutter={[8, 8]}>
                          {modelOptions.map(model => (
                            <Col xs={24} key={`series-pm-${model.id}`}>
                              <Checkbox value={model.id}>{model.id}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Evaluator Panel ({evaluatorModels.length}/6)</Text>
                      <Checkbox.Group value={evaluatorModels} onChange={values => setEvaluatorModels(values as string[])} style={{ width: '100%', marginTop: 12 }}>
                        <Row gutter={[8, 8]}>
                          {modelOptions.map(model => (
                            <Col xs={24} key={`series-em-${model.id}`}>
                              <Checkbox value={model.id}>{model.id}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Col>
                  </Row>
                </Card>

                <Card className="ai-testing-section-card ai-testing-run-card">
                  <Space wrap>
                    <Button
                      type="primary"
                      size="large"
                      icon={<RocketOutlined />}
                      loading={loading}
                      onClick={handleCreateAndStartSeries}
                    >
                      Create and Start Tiered Test
                    </Button>
                    {activeSeries?.status === 'paused' && !activeSeries.legacy_read_only && (
                      <Button type="primary" onClick={handleContinueSeries} loading={loading}>
                        Continue Series
                      </Button>
                    )}
                    {activeSeries && !['completed', 'stopped', 'failed'].includes(activeSeries.status) && (
                      <Button danger icon={<StopOutlined />} onClick={handleStopSeries} loading={loading}>
                        Stop Series
                      </Button>
                    )}
                  </Space>
                </Card>

                <Card title="Recent Tiered Series" className="ai-testing-section-card">
                  <Table
                    rowKey="id"
                    size="small"
                    dataSource={seriesRows}
                    pagination={{ pageSize: 5 }}
                    columns={[
                      { title: 'Name', dataIndex: 'name' },
                      { title: 'Status', dataIndex: 'status', render: value => <Tag>{String(value || 'draft')}</Tag> },
                      { title: 'Phase', dataIndex: 'current_phase', render: value => seriesPhaseLabel(String(value || '')) },
                      { title: 'Cost', dataIndex: 'total_cost_usd', render: value => `$${formatMetric(value || 0, 4)}` },
                      {
                        title: '',
                        key: 'open',
                        render: (_, row: ExperimentSeries) => (
                          <Space size="small">
                            <Button size="small" onClick={() => handleOpenSeries(row)} loading={loading}>
                              Open
                            </Button>
                            <Tooltip title="Download summary report">
                              <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => handleExportSeriesReport('summary', row)}
                                loading={loading}
                              >
                                Summary
                              </Button>
                            </Tooltip>
                            <Tooltip title="Delete series">
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteSeries(row)}
                                disabled={!['completed', 'stopped', 'failed'].includes(row.status)}
                                loading={loading}
                              />
                            </Tooltip>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>

                {seriesProgress && (
                  <Card title="Series Dashboard" className="ai-testing-section-card">
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <Space wrap>
                        <Tag>Status: {seriesProgress.status}</Tag>
                        <Tag>Current: {seriesPhaseLabel(seriesProgress.current_phase)}</Tag>
                        {seriesProgress.status !== 'completed' && <Tag>Next: {seriesPhaseLabel(seriesProgress.next_phase)}</Tag>}
                        <Tag>Total cost: ${formatMetric(seriesProgress.total_cost_usd || 0, 4)}</Tag>
                        <Tag>Current phase cost: ${formatMetric(seriesProgress.current_phase_cost_usd || 0, 4)}{seriesProgress.current_phase_cost_status ? ` (${seriesProgress.current_phase_cost_status})` : ''}</Tag>
                        {seriesProgress.status !== 'completed' && <Tag>Projected next cells: {seriesProgress.projected_next_phase_cells || 0}</Tag>}
                        {seriesProgress.status !== 'completed' && <Tag>Projected next cost: ${formatMetric(seriesProgress.projected_next_phase_cost_usd || 0, 4)}</Tag>}
                      </Space>
                      {renderSeriesDecisionPanel(seriesProgress)}
                      {activeSeries?.pause_reason && (
                        <Alert type="warning" showIcon message="Series paused" description={activeSeries.pause_reason} />
                      )}
                      {activeSeries?.legacy_read_only && (
                        <Alert
                          type="warning"
                          showIcon
                          message="Legacy series"
                          description="Old-order tiered series are view/export only. Start a new series to use the refactored raw translation, proofreader combo, and loop sweep flow."
                        />
                      )}
                      {seriesProgress.current_experiment_progress && (
                        <div>
                          <Text strong>Current Phase Progress</Text>
                          <Progress percent={seriesProgress.current_experiment_progress.progress_percentage || 0} />
                          <Space wrap>
                            {Object.entries(seriesProgress.current_experiment_progress.counts || {}).map(([key, value]) => (
                              <Tag key={`series-count-${key}`}>{key}: {String(value)}</Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                      <Table
                        rowKey="phase"
                        size="small"
                        pagination={false}
                        dataSource={seriesProgress.phases || []}
                        columns={[
                          { title: 'Phase', dataIndex: 'label' },
                          { title: 'Status', dataIndex: 'status', render: value => <Tag>{String(value || 'pending')}</Tag> },
                          {
                            title: 'Decision',
                            key: 'decision',
                            render: (_, row) => renderPhaseOutcome(row.phase, row.result),
                          },
                          {
                            title: 'Top Results',
                            dataIndex: 'result',
                            render: result => renderSeriesCostPreview(result),
                          },
                          {
                            title: 'Cost',
                            dataIndex: 'result',
                            render: result => {
                              if (!result) return <Text type="secondary">n/a</Text>
                              return `$${formatMetric(result.cost_total_usd || 0, 4)}${result.partial ? ' partial' : ''}`
                            },
                          },
                        ]}
                        expandable={{
                          expandedRowRender: row => renderSeriesCandidateCosts(row.result, {
                            loading,
                            onPromoteCandidate: activeSeries?.legacy_read_only ? undefined : (phase, candidate) => void handleManualPromotion(phase, 'add', candidate),
                            onPromoteAll: activeSeries?.legacy_read_only ? undefined : phase => void handleManualPromotion(phase, 'all'),
                            onClearPromotions: activeSeries?.legacy_read_only ? undefined : phase => void handleManualPromotion(phase, 'clear'),
                          }),
                          rowExpandable: row => Boolean((row.result?.candidate_costs || []).length),
                        }}
                      />
                      <Space wrap>
                        {activeSeries?.status === 'paused' && !activeSeries.legacy_read_only && (
                          <Button type="primary" onClick={handleContinueSeries} loading={loading}>Continue Series</Button>
                        )}
                        {activeSeries && !['completed', 'stopped', 'failed'].includes(activeSeries.status) && (
                          <Button danger icon={<StopOutlined />} onClick={handleStopSeries} loading={loading}>Stop Series</Button>
                        )}
                        <Button onClick={() => activeSeries && refreshSeriesProgress(activeSeries)} loading={loading}>Refresh Series</Button>
                        <Button onClick={() => handleExportSeriesReport('summary')} disabled={!activeSeries} loading={loading}>Export Series Summary JSON</Button>
                        <Button onClick={() => handleExportSeriesReport('detailed')} disabled={!activeSeries} loading={loading}>Export Series Detailed JSON</Button>
                      </Space>
                    </Space>
                  </Card>
                )}
              </Space>
            ),
          },
          {
            key: 'experiment',
            label: 'New Experiment',
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Experiment Setup" className="ai-testing-section-card">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Text strong>Target Language</Text>
                      <Select value={language} onChange={setLanguage} style={{ width: '100%', marginTop: 8 }}>
                        {LANGUAGES.map(item => <Option key={item} value={item}>{item}</Option>)}
                      </Select>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Benchmark Pack</Text>
                      <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                        <Select
                          value={selectedPackId}
                          onChange={setSelectedPackId}
                          style={{ width: '100%' }}
                          placeholder="Select a pack"
                        >
                          {packs.map(pack => (
                            <Option key={pack.id} value={pack.id}>{pack.name} ({pack.version})</Option>
                          ))}
                        </Select>
                        <Tooltip title="Delete selected pack">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={!selectedPack}
                            onClick={handleDeleteSelectedPack}
                          />
                        </Tooltip>
                      </Space.Compact>
                    </Col>
                    <Col xs={24} md={8}>
                      <Space size={4}>
                        <Text strong>Preset</Text>
                        <Tooltip title={selectedPresetInfo.detail}>
                          <InfoCircleOutlined style={{ color: '#53667a' }} />
                        </Tooltip>
                      </Space>
                      <Select
                        value={preset}
                        onChange={(value: ExperimentPreset) => setPreset(value)}
                        optionLabelProp="label"
                        style={{ width: '100%', marginTop: 8 }}
                      >
                        {PRESET_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value} label={option.label} title={option.detail}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                      <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                        {selectedPresetInfo.short}
                      </Text>
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Experiment Name</Text>
                      <Input value={experimentName} onChange={event => setExperimentName(event.target.value)} style={{ marginTop: 8 }} />
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Run Override</Text>
                      <InputNumber min={1} max={10} value={numRuns} onChange={value => setNumRuns(value || undefined)} style={{ width: '100%', marginTop: 8 }} placeholder="Use preset default" />
                    </Col>
                  </Row>
                  {selectedPack && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="info"
                      showIcon
                      message={`${selectedPack.item_count || selectedPack.items?.length || 0} benchmark items selected`}
                      description={selectedPack.description || 'Versioned benchmark pack will be snapshotted into the experiment.'}
                    />
                  )}
                </Card>

                <Card title="Recent Experiments" className="ai-testing-section-card">
                  <Table
                    rowKey="id"
                    size="small"
                    dataSource={experiments}
                    pagination={{ pageSize: 5 }}
                    columns={[
                      { title: 'Name', dataIndex: 'name' },
                      { title: 'Status', dataIndex: 'status', render: value => <Tag>{String(value || 'draft')}</Tag> },
                      { title: 'Cells', dataIndex: 'total_cells' },
                      { title: 'Created', dataIndex: 'created_at', render: value => value ? new Date(String(value)).toLocaleString() : '' },
                      {
                        title: '',
                        key: 'open',
                        render: (_, row: Experiment) => (
                          <Button size="small" onClick={() => handleOpenExperiment(row)} loading={loading}>
                            Open
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Card>

                <Card title="Models and Judges" className="ai-testing-section-card">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Text strong>Translation Models</Text>
                      <Checkbox.Group value={translationModels} onChange={values => setTranslationModels(values as string[])} style={{ width: '100%', marginTop: 12 }}>
                        <Row gutter={[8, 8]}>
                          {modelOptions.map(model => (
                            <Col xs={24} md={12} key={`tm-${model.id}`}>
                              <Checkbox value={model.id}>{model.id}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Evaluator Panel ({evaluatorModels.length}/6)</Text>
                      <Checkbox.Group value={evaluatorModels} onChange={values => setEvaluatorModels(values as string[])} style={{ width: '100%', marginTop: 12 }}>
                        <Row gutter={[8, 8]}>
                          {modelOptions.map(model => (
                            <Col xs={24} md={12} key={`em-${model.id}`}>
                              <Checkbox value={model.id}>{model.id}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Col>
                    <Col span={24}>
                      <Text strong>Rules Variant Instructions</Text>
                      <Input.TextArea
                        rows={4}
                        value={extraInstruction}
                        onChange={event => setExtraInstruction(event.target.value)}
                        placeholder="Optional extra translation/system rules to test against the baseline."
                        style={{ marginTop: 8 }}
                      />
                    </Col>
                    <Col span={24}>
                      <Space size={4}>
                        <Text strong>Proofreader Model Matrix</Text>
                        <Tooltip title="Optional. Select models to test as the shared model for both the formality pass and the general proofreading pass. Leave blank to use production proofreader defaults. Alignment is always enabled for loop optimization and uses the fixed alignment model below if selected.">
                          <InfoCircleOutlined style={{ color: '#53667a' }} />
                        </Tooltip>
                      </Space>
                      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                        <Col xs={24} md={16}>
                          <Text type="secondary">Formality + general proofreading</Text>
                          <Select
                            mode="multiple"
                            showSearch
                            placeholder="Production default, or select proofreader models to compare"
                            value={proofreaderTestModels}
                            onChange={setProofreaderTestModels}
                            optionFilterProp="children"
                            style={{ width: '100%', marginTop: 6 }}
                          >
                            {modelOptions.map(model => (
                              <Option key={`pm-${model.id}`} value={model.id}>{model.id}</Option>
                            ))}
                          </Select>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Alignment check</Text>
                          <Select
                            allowClear
                            showSearch
                            placeholder="Production default"
                            value={proofreaderAlignmentModel}
                            onChange={setProofreaderAlignmentModel}
                            optionFilterProp="children"
                            style={{ width: '100%', marginTop: 6 }}
                          >
                            {modelOptions.map(model => (
                              <Option key={`pa-${model.id}`} value={model.id}>{model.id}</Option>
                            ))}
                          </Select>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>

                <Card className="ai-testing-section-card ai-testing-run-card">
                  <Button
                    type="primary"
                    size="large"
                    icon={<RocketOutlined />}
                    loading={loading}
                    onClick={handleCreateAndRunExperiment}
                  >
                    Create and Run Experiment
                  </Button>
                </Card>

                {progress && (
                  <Card title="Live Progress" className="ai-testing-section-card">
                    <Progress percent={progress.progress_percentage || 0} />
                    <Space wrap>
                      {Object.entries(progress.counts || {}).map(([key, value]) => (
                        <Tag key={key}>{key}: {String(value)}</Tag>
                      ))}
                      {Number(progress.counts?.planned || 0) > 0 && activeExperiment && (
                        <Button size="small" onClick={handleResumeQueueing} loading={loading}>
                          Resume Queueing
                        </Button>
                      )}
                    </Space>
                    {(progress.status === 'canceled' || progress.status === 'cancelled') && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type="info"
                        showIcon
                        message="Experiment stopped."
                        description="Canceled cells will not spend additional model calls. Any cell already inside a model request may still finish and report back."
                      />
                    )}
                    {Number(progress.counts?.planned || 0) > 0 && !Number(progress.counts?.queued || 0) && !Number(progress.counts?.running || 0) && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type="warning"
                        showIcon
                        message="Some experiment cells have not been queued yet."
                        description="Use Resume Queueing to create the remaining worker tasks."
                      />
                    )}
                    {Number(progress.counts?.queue_failed || 0) > 0 && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type="error"
                        showIcon
                        message={`${progress.counts.queue_failed} cells failed while creating Cloud Tasks.`}
                        description={
                          <Space direction="vertical" size={8}>
                            {(progress.queue_errors || []).length ? (
                              (progress.queue_errors || []).map(error => (
                                <div key={error.message}>
                                  <Text strong>{error.count}x </Text>
                                  <Text>{error.message}</Text>
                                  {!!error.examples?.length && (
                                    <div>
                                      <Text type="secondary">
                                        Examples: {error.examples.map(item => `${item.model || 'model'} / ${item.variant || 'variant'} / ${item.text || item.run_id || 'run'}`).join('; ')}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <Text>Refresh progress after restarting the backend to load queue error details.</Text>
                            )}
                          </Space>
                        }
                      />
                    )}
                  </Card>
                )}

                {activeExperiment && (
                  <Card title="Report" className="ai-testing-section-card">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space wrap>
                        <Button onClick={handleRefreshReport} loading={loading}>Refresh Report</Button>
                        <Button onClick={() => handleExportReport('summary')} disabled={!activeExperiment} loading={loading}>Export Summary JSON</Button>
                        <Button onClick={() => handleExportReport('detailed')} disabled={!activeExperiment} loading={loading}>Export Detailed JSON</Button>
                      </Space>
                      {report?.recommended_action ? (
                        <Alert
                          type="success"
                          showIcon
                          message={`Recommended: ${report.recommended_action.model || 'none'} / ${report.recommended_action.variant || 'n/a'}`}
                          description={report.recommended_action.reason}
                          action={
                            report.recommended_action.model ? (
                              <Button onClick={handleApply} loading={loading}>Apply From Testing</Button>
                            ) : undefined
                          }
                        />
                      ) : (
                        <Alert type="info" showIcon message="Run or refresh the report after experiment cells complete." />
                      )}
                      <Alert
                        type="info"
                        showIcon
                        message={report?.evaluation_mode === DECISION_EVALUATION_MODE ? 'Decision method' : 'Scoring method'}
                        description={report?.evaluation_mode === DECISION_EVALUATION_MODE
                          ? 'Each completed cell is judged by the evaluator panel and receives hard-gate issue counts instead of a numeric score. Ranking treats diagnostics, critical errors, publication blockers, gate misses, and issue rates as penalties; pairwise judging and pipeline cost break close comparisons. Legacy score fields are intentionally not used.'
                          : 'Each completed cell is judged by the selected evaluator panel. Category scores from judge responses are averaged into a per-run score, then candidate rows average those scored runs. Baseline delta is the candidate average minus the selected baseline model/variant; +/-0.15 is treated as a tie.'}
                      />
                      <Table
                        rowKey="key"
                        size="small"
                        dataSource={rankCandidateRows((report?.candidates || []) as CandidateCostSummary[], report?.evaluation_mode === DECISION_EVALUATION_MODE).map((row, index) => ({ ...row, rank: index + 1 }))}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        columns={[
                          ...(report?.evaluation_mode === DECISION_EVALUATION_MODE ? [
                            {
                              title: 'Rank',
                              dataIndex: 'rank',
                              render: (value: unknown) => <Tag color={Number(value) === 1 ? 'gold' : Number(value) <= 3 ? 'blue' : 'default'}>#{Number(value || 0)}</Tag>,
                            },
                            {
                              title: 'Quality',
                              key: 'quality',
                              render: (_: unknown, row: CandidateCostSummary) => renderCompactDecisionQuality(row),
                            },
                          ] : []),
                          { title: 'Model', dataIndex: 'model' },
                          { title: 'Variant', dataIndex: 'variant' },
                          ...(report?.evaluation_mode === DECISION_EVALUATION_MODE ? [
                            {
                              title: 'Gates',
                              key: 'gates',
                              render: (_: unknown, row: CandidateCostSummary) => renderCompactGateSummary(row),
                            },
                            {
                              title: 'Penalty',
                              key: 'penalty',
                              render: (_: unknown, row: CandidateCostSummary) => renderCompactDecisionPenalty(row),
                            },
                            {
                              title: 'Pairwise',
                              key: 'pairwise',
                              render: (_: unknown, row: CandidateCostSummary) => renderPairwiseResult(row),
                            },
                            {
                              title: 'Decision',
                              dataIndex: 'decision_status',
                              render: (value: unknown) => <Tag color={decisionStatusColor(String(value || ''))}>{String(value || 'unknown').replace(/_/g, ' ')}</Tag>,
                            },
                          ] : [
                            {
                              title: 'Score',
                              dataIndex: 'average_score',
                              render: (value: unknown, row: CandidateCostSummary) => Number(row.sample_count || 0) > 0 ? Number(value || 0).toFixed(2) : <Text type="secondary">No scored runs</Text>,
                            },
                            { title: 'vs Baseline', dataIndex: 'pairwise_vs_baseline' },
                            {
                              title: 'Delta',
                              dataIndex: 'baseline_delta',
                              render: (value: unknown, row: CandidateCostSummary) => Number(row.sample_count || 0) > 0 ? Number(value || 0).toFixed(2) : <Text type="secondary">n/a</Text>,
                            },
                          ]),
                          {
                            title: report?.evaluation_mode === DECISION_EVALUATION_MODE ? 'Evaluator coverage' : 'Runs',
                            dataIndex: 'sample_count',
                            render: (value, row) => report?.evaluation_mode === DECISION_EVALUATION_MODE
                              ? (
                                <Space direction="vertical" size={0}>
                                  <Text>{row.completed_count || value || 0}/{row.run_count || value || 0} runs evaluated</Text>
                                  <Text type="secondary">
                                    {row.gate_result_count || 0} hard-gate judgments · {formatMetric(row.avg_evaluators_per_run, 1)} evaluators/run
                                  </Text>
                                </Space>
                              )
                              : `${row.scored_run_count || 0}/${value || 0} scored`,
                          },
                          {
                            title: 'Cost',
                            dataIndex: 'cost_total_usd',
                            render: (value, row) => {
                              if (!Number(row.sample_count || 0)) return <Text type="secondary">n/a</Text>
                              return (
                                <Space direction="vertical" size={0}>
                                  <Text>${formatMetric(value || 0, 4)}</Text>
                                  <Text type="secondary">
                                    {typeof pipelineCostPer10k(row) === 'number' ? `$${formatMetric(pipelineCostPer10k(row), 4)}/10k pipeline` : 'n/a /10k pipeline'}
                                  </Text>
                                </Space>
                              )
                            },
                          },
                          {
                            title: 'Diagnostics',
                            dataIndex: 'diagnostic_block_rate',
                            render: (value, row) => {
                              if (!Number(row.sample_count || 0)) {
                                return <Text type="secondary">No completed samples</Text>
                              }
                              const blockers = row.diagnostic_blockers || []
                              const warnings = row.diagnostic_warnings || []
                              return (
                                <Tooltip
                                  color="#ffffff"
                                  overlayInnerStyle={{
                                    color: '#102033',
                                    maxWidth: 420,
                                    padding: '12px 14px',
                                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
                                  }}
                                  title={
                                    <Space direction="vertical" size={8}>
                                      <div>
                                        <Text strong style={{ color: '#102033' }}>Blockers</Text>
                                        <div style={{ marginTop: 4 }}>{renderDiagnosticDetails(blockers, { tooltip: true })}</div>
                                      </div>
                                      <div>
                                        <Text strong style={{ color: '#102033' }}>Warnings</Text>
                                        <div style={{ marginTop: 4 }}>{renderDiagnosticDetails(warnings, { tooltip: true })}</div>
                                      </div>
                                    </Space>
                                  }
                                >
                                  <Space direction="vertical" size={2}>
                                    <Text>{Math.round(Number(value || 0) * 100)}% blocked</Text>
                                    {blockers[0] && (
                                      <Text type="secondary">{diagnosticLabel(blockers[0].reason)} ({blockers[0].count})</Text>
                                    )}
                                    {!blockers.length && warnings[0] && (
                                      <Text type="secondary">Warn: {diagnosticLabel(warnings[0].reason)} ({warnings[0].count})</Text>
                                    )}
                                  </Space>
                                </Tooltip>
                              )
                            },
                          },
                        ]}
                        expandable={{
                          expandedRowRender: row => (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                              {renderScoringDetails(row)}
                              <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                  <Text strong>Blocking Diagnostics</Text>
                                  <div style={{ marginTop: 8 }}>{renderDiagnosticDetails(row.diagnostic_blockers)}</div>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Text strong>Warning Diagnostics</Text>
                                  <div style={{ marginTop: 8 }}>{renderDiagnosticDetails(row.diagnostic_warnings)}</div>
                                </Col>
                              </Row>
                            </Space>
                          ),
                          rowExpandable: () => true,
                        }}
                      />
                    </Space>
                  </Card>
                )}
              </Space>
            ),
          },
          {
            key: 'legacy',
            label: 'Legacy Results',
            children: (
              <Card title="Read-only legacy AI testing results" className="ai-testing-section-card">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Legacy results are preserved for review/export. New runs should use the experiment workflow."
                />
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={legacyResults}
                  columns={[
                    { title: 'Created', dataIndex: 'created_at' },
                    { title: 'Test', dataIndex: 'test_name' },
                    { title: 'Language', dataIndex: 'language' },
                    { title: 'Text', dataIndex: 'text_name' },
                    { title: 'Models', dataIndex: 'models', render: value => (value || []).join(', ') },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Create Benchmark Pack"
        visible={packModalOpen}
        onCancel={() => setPackModalOpen(false)}
        footer={null}
        width={780}
      >
        <Form form={packForm} layout="vertical" onFinish={handleCreatePack}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="French dialogue regression pack" />
          </Form.Item>
          <Form.Item name="version" label="Version" initialValue="v1">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="itemsJson"
            label="Items JSON"
            initialValue={JSON.stringify(defaultBenchmarkItems, null, 2)}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={12} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Create Pack</Button>
            <Button onClick={() => setPackModalOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Generate Full-Chapter Benchmark Pack"
        visible={chapterPackModalOpen}
        onCancel={() => setChapterPackModalOpen(false)}
        footer={null}
        width={780}
      >
        <Form form={chapterPackForm} layout="vertical" onFinish={handleGenerateChapterPack}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="French full chapters random pack" />
          </Form.Item>
          <Form.Item
            name="sourceBookIds"
            label="Source Books"
            rules={[{ required: true, message: 'Select at least one source book' }]}
            extra={
              chapterSourceBooks.length ? (
                <Space wrap size={[4, 4]}>
                  <Tag color="green">{chapterSourceBookMetadataCounts.ready} ready</Tag>
                  <Tag color="gold">{chapterSourceBookMetadataCounts.partial} partial</Tag>
                  <Tag>{chapterSourceBookMetadataCounts.missing} missing</Tag>
                  <Text type="secondary">Books with stronger metadata are listed first.</Text>
                </Space>
              ) : undefined
            }
          >
            <Select
              mode="multiple"
              placeholder="Select uploaded books from your admin account"
              loading={chapterSourceBooksLoading}
              optionFilterProp="label"
              showSearch
              maxTagCount="responsive"
            >
              {chapterSourceBooks.map(book => (
                <Option
                  key={book.id}
                  value={book.id}
                  label={`${book.title} ${book.id} ${book.metadata_status || ''} ${(book.metadata_missing || []).join(' ')} ${(book.metadata_fields?.quotation_styles_set_languages || []).join(' ')} ${(book.metadata_fields?.quotation_styles_missing_languages || []).join(' ')}`}
                >
                  <Space direction="vertical" size={0}>
                    <Space wrap size={[6, 0]}>
                      <Text>{book.title}</Text>
                      <Tag color={metadataStatusColor(book.metadata_status)}>
                        {metadataStatusLabel(book.metadata_status)}
                      </Tag>
                      {typeof book.metadata_score === 'number' && (
                        <Tag>{book.metadata_score}/5</Tag>
                      )}
                      {typeof book.metadata_fields?.quotation_styles_required_count === 'number' && book.metadata_fields.quotation_styles_required_count > 0 ? (
                        <Tooltip color="white" title={renderQuotationStyleCoverageTooltip(book)}>
                          <Tag color={book.metadata_fields.quotation_styles ? 'green' : 'gold'}>
                            {book.metadata_fields.quotation_styles_set_count || 0}/{book.metadata_fields.quotation_styles_required_count} quote styles
                          </Tag>
                        </Tooltip>
                      ) : null}
                    </Space>
                    <Text type="secondary">
                      {book.eligible_chapter_count || 0}/{book.chapter_count || 0} eligible chapters
                      {book.word_count_total ? ` · ${book.word_count_total.toLocaleString()} words` : ''}
                    </Text>
                    {book.metadata_missing?.length ? (
                      <Text type="secondary">
                        Missing: {book.metadata_missing.map(item => metadataMissingLabel(item, book)).join(', ')}
                      </Text>
                    ) : null}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="version" label="Version" initialValue="v1">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="minWordCount" label="Minimum Words" initialValue={1000}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="maxChapters" label="Chapter Count" initialValue={5}>
                <InputNumber min={1} max={25} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="randomSeed" label="Random Seed">
                <Input placeholder="Optional repeatable sample seed" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="maxBooksToScan" label="Max Books To Scan" initialValue={100}>
                <InputNumber min={1} max={500} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button
            icon={<ReloadOutlined />}
            loading={chapterSourceBooksLoading}
            onClick={() => loadChapterSourceBooks()}
            style={{ marginBottom: 16 }}
          >
            Refresh Books
          </Button>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Generated packs are reusable across target languages. They store chapter references and resolve full text when experiments are queued."
          />
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>Generate Pack</Button>
            <Button onClick={() => setChapterPackModalOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  )
}

export default AIExperimentTestingTab
